/**
 * BLRRD Persistence — Files store.
 *
 * IndexedDB CRUD for original image bytes + thumbnail. Keyed by content
 * hash (FNV-1a-32 over the file Blob + dims, computed by the legacy
 * editor at image-load) so the same image loaded twice de-dupes cleanly.
 *
 *   files:  { hash, name, mime, size, width, height, createdAt, blob, thumbnail }
 *
 * Codex Round 2 hardening:
 *   R2-3  All cross-store mutations (deleteFile, clearFiles) go through a
 *         single multi-store readwrite transaction over ['files','projects']
 *         so a partial failure cannot orphan one side or the other.
 *   R2-4  Helpers resolve on `tx.oncomplete`, not `request.onsuccess`.
 *         oncomplete is the actual commit barrier; onsuccess only signals
 *         that the operation queued cleanly.
 *
 * Privacy invariant: blobs never leave the browser. The Files store IS
 * the user's photo library, on-device only.
 */

import { openDB } from './idb.js';

const STORE = 'files';

/* ── helpers ───────────────────────────────────────────────────────── */

/* Wait for transaction completion (true commit barrier). Resolves with
 * `result` (whatever the inner request returned via onsuccess). */
function awaitTx(tx, result) {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error || new Error('IDB transaction failed'));
        tx.onabort = () => reject(tx.error || new Error('IDB transaction aborted'));
    });
}

/* ── public API ────────────────────────────────────────────────────── */

/**
 * Save a file record. Overwrites if the same hash already exists.
 * Resolves only after the transaction commits (Codex R2-4).
 *
 * @param {object} record
 *   @prop {string} hash         — `E.imgKey` (dim-prefix + FNV-1a-32 hex)
 *   @prop {string} name         — original filename or 'untitled'
 *   @prop {string} mime         — image/jpeg | image/png | image/webp | …
 *   @prop {number} size         — byte length of `blob`
 *   @prop {number} width
 *   @prop {number} height
 *   @prop {Blob}   blob         — original file bytes (for re-decode on reopen)
 *   @prop {Blob | null} thumbnail — small JPEG (default 256×256), null on decode failure
 * @returns {Promise<string>}    the stored hash
 */
export async function saveFile(record) {
    if (!record || !record.hash) throw new Error('saveFile: missing hash');
    const db = await openDB();
    try {
        const stored = { ...record, createdAt: record.createdAt || Date.now() };
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(stored);
        return await awaitTx(tx, stored.hash);
    } finally {
        db.close();
    }
}

/** Load a file by hash, or null. */
export async function getFile(hash) {
    if (!hash) return null;
    const db = await openDB();
    try {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).get(hash);
        let value = null;
        req.onsuccess = () => { value = req.result || null; };
        await awaitTx(tx);
        return value;
    } finally {
        db.close();
    }
}

/**
 * List all files, newest first. Returned objects include `thumbnail` Blob
 * but NOT the full `blob` (saves heap when rendering a grid). Use
 * `getFile(hash)` to retrieve the full blob.
 */
export async function listFiles() {
    const db = await openDB();
    try {
        const tx = db.transaction(STORE, 'readonly');
        const idx = tx.objectStore(STORE).index('createdAt');
        const out = [];
        const cursor = idx.openCursor(null, 'prev');
        cursor.onsuccess = (e) => {
            const c = e.target.result;
            if (!c) return;
            const { hash, name, mime, size, width, height, createdAt, thumbnail } = c.value;
            out.push({ hash, name, mime, size, width, height, createdAt, thumbnail });
            c.continue();
        };
        await awaitTx(tx);
        return out;
    } finally {
        db.close();
    }
}

/**
 * Delete a file by hash. **Atomic** across files + projects: a single
 * readwrite transaction iterates the projects.fileHash index, deletes
 * matching project rows, then deletes the file row. If anything throws,
 * the transaction aborts and nothing is committed (Codex R2-3).
 */
export async function deleteFile(hash) {
    if (!hash) return false;
    const db = await openDB();
    try {
        const tx = db.transaction([STORE, 'projects'], 'readwrite');
        const filesStore    = tx.objectStore(STORE);
        const projectsStore = tx.objectStore('projects');
        const idx = projectsStore.index('fileHash');
        const cursor = idx.openCursor(IDBKeyRange.only(hash));
        cursor.onsuccess = (e) => {
            const c = e.target.result;
            if (c) { c.delete(); c.continue(); return; }
            /* All matching projects deleted — delete the file. */
            filesStore.delete(hash);
        };
        await awaitTx(tx);
        return true;
    } finally {
        db.close();
    }
}

/**
 * Clear all files **and all projects** in a single multi-store transaction.
 * This is the privacy "Wipe library" action — Codex R2-3 fix: includes
 * orphan projects (projects whose fileHash never matched any file).
 */
export async function clearFiles() {
    const db = await openDB();
    try {
        const tx = db.transaction([STORE, 'projects'], 'readwrite');
        tx.objectStore(STORE).clear();
        tx.objectStore('projects').clear();
        await awaitTx(tx);
    } finally {
        db.close();
    }
}
